import { useEffect, useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import moment from 'moment-jalaali';
import gsap from 'gsap';

import { getUserProfile, updateUserProfile } from '../../api/users.api';
import { manualRenewSubscription } from '../../api/subscriptions.api';

import ReportsTab from './tabs/ReportsTab';
import PhotosTab from './tabs/PhotosTab';
import WeightTab from './tabs/WeightTab';
import MeasurementsTab from './tabs/MeasurementsTab';
import AssessmentTab from './tabs/AssessmentTab';
import AttachmentsTab from './tabs/AttachmentsTab';

export default function UserDetails() {
  const { userId } = useParams();

  const [profile, setProfile] = useState(null);
  const [activeTab, setActiveTab] = useState('reports');
  const [editing, setEditing] = useState(false);

  const editBoxRef = useRef(null);

  const [form, setForm] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    subscriptionType: '',
    maintainCalorie: '',
  });

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      const res = await getUserProfile(userId);
      const data = res.data;
      setProfile(data);

      // فرم اولیه را پر کنیم
      setForm({
        name: data.name || '',
        email: data.email || '',
        phoneNumber: data.phoneNumber || '',
        subscriptionType: data.subscriptionType || '',
        maintainCalorie: data.maintainCalorie || '',
      });
    } catch (err) {
      console.error('Error loading profile:', err);
    }
  };

  const handleRenew = async (type, days) => {
    const confirmMessage = `آیا از فعال‌سازی دوره "${type}" به مدت ${days} روز برای این کاربر مطمئن هستید؟`;
    if (!window.confirm(confirmMessage)) return;

    try {
      await manualRenewSubscription({
        userId: userId,
        productType: type,
        days: days, // ارسال تعداد روزهای انتخابی
      });
      alert('اشتراک با موفقیت تمدید شد.');
      loadProfile(); // بروزرسانی اطلاعات صفحه
    } catch (err) {
      alert('خطا در تمدید اشتراک');
    }
  };
  // انیمیشن باز شدن با GSAP
  useEffect(() => {
    if (editing) {
      gsap.fromTo(
        editBoxRef.current,
        { height: 0, opacity: 0 },
        { height: 'auto', opacity: 1, duration: 0.4, ease: 'power2.out' },
      );
    }
  }, [editing]);

  if (!profile) return <p>در حال بارگذاری...</p>;

  const saveChanges = async () => {
    try {
      await updateUserProfile(userId, form);
      alert('تغییرات با موفقیت ذخیره شد');

      setEditing(false);
      loadProfile();
    } catch (err) {
      console.error(err);
      alert('خطا در ذخیره تغییرات');
    }
  };

  const fullName = profile.name || 'نام نامشخص';
  const avatarLetter = fullName.charAt(0);

  return (
    <div className="max-w-5xl mx-auto">
      {/* ---------------- Summary Card ---------------- */}
      <div className="bg-white border rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-6">
          {profile.photo ? (
            <img
              src={profile.photo}
              className="w-32 h-32 rounded-full bg-transparent object-cover border-1 flex items-center justify-center "
            />
          ) : (
            <div className="w-20 h-20 rounded-full bg-gray-200 flex items-center justify-center text-3xl font-bold text-gray-700">
              {avatarLetter}
            </div>
          )}

          {/* User info */}
          <div className="flex-1">
            <h2 className="text-xl font-bold mb-2">{fullName}</h2>

            <div className="text-gray-600 text-sm space-y-1">
              <p>موبایل: {profile.phoneNumber || '-'}</p>
              <p>ایمیل: {profile.email || '-'}</p>
              <p>نوع اشتراک: {profile.subscriptionType || '-'}</p>
              <p>کالری تثبیت: {profile.maintainCalorie || '-'}</p>
              <p>
                تاریخ عضویت:{' '}
                {profile.createdAt
                  ? moment(profile.createdAt).format('jYYYY/jMM/jDD')
                  : '-'}
              </p>
            </div>
          </div>

          {/* Edit button */}
          <button
            onClick={() => setEditing(!editing)}
            className="px-4 py-2 bg-blue-600 text-white rounded-xl">
            {editing ? 'بستن ادیت' : 'ویرایش پروفایل'}
          </button>
        </div>

        <div className="mt-6 p-5 bg-blue-50 border border-blue-100 rounded-2xl shadow-sm">
          <h4 className="text-sm font-bold text-blue-900 mb-4 flex items-center gap-2">
            <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
            تمدید و تغییر وضعیت اشتراک کاربر
          </h4>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {/* گزینه خصوصی */}
            <button
              onClick={() => handleRenew('private', 30)}
              className="flex flex-col items-center justify-center p-3 bg-white border border-purple-200 rounded-xl hover:bg-purple-50 transition shadow-sm group">
              <span className="text-xs font-bold text-purple-700">
                دوره خصوصی
              </span>
              <span className="text-[10px] text-purple-400 font-medium mt-1">
                ۳۰ روزه
              </span>
            </button>

            {/* گزینه آکادمی */}
            <button
              onClick={() => handleRenew('academy', 90)}
              className="flex flex-col items-center justify-center p-3 bg-white border border-blue-200 rounded-xl hover:bg-blue-50 transition shadow-sm group">
              <span className="text-xs font-bold text-blue-700">
                دوره آکادمی
              </span>
              <span className="text-[10px] text-blue-400 font-medium mt-1">
                ۳ ماهه (۹۰ روز)
              </span>
            </button>

            {/* گزینه پرو */}
            <button
              onClick={() => handleRenew('pro', 90)}
              className="flex flex-col items-center justify-center p-3 bg-white border border-emerald-200 rounded-xl hover:bg-emerald-50 transition shadow-sm group">
              <span className="text-xs font-bold text-emerald-700">
                دوره پرو
              </span>
              <span className="text-[10px] text-emerald-400 font-medium mt-1">
                ۳ ماهه (۹۰ روز)
              </span>
            </button>
          </div>
        </div>

        {/* Edit box */}
        {editing && (
          <div ref={editBoxRef} className="overflow-hidden mt-6 border-t pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                className="border p-2 rounded-xl"
                placeholder="نام"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />

              <input
                className="border p-2 rounded-xl"
                placeholder="ایمیل"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />

              <input
                className="border p-2 rounded-xl"
                placeholder="شماره موبایل"
                value={form.phoneNumber}
                onChange={(e) =>
                  setForm({ ...form, phoneNumber: e.target.value })
                }
              />

              <input
                className="border p-2 rounded-xl"
                placeholder="نوع اشتراک (pro / academy / ...)"
                value={form.subscriptionType}
                onChange={(e) =>
                  setForm({ ...form, subscriptionType: e.target.value })
                }
              />

              <input
                className="border p-2 rounded-xl"
                placeholder="کالری تثبیت (maintainCalories)"
                type="number"
                value={form.maintainCalorie}
                onChange={(e) =>
                  setForm({ ...form, maintainCalorie: e.target.value })
                }
              />
            </div>

            <button
              onClick={saveChanges}
              className="mt-4 px-5 py-2 bg-green-600 text-white rounded-xl">
              ذخیره تغییرات
            </button>
          </div>
        )}
      </div>

      {/* ---------------- Tabs ---------------- */}
      <div className="flex gap-3 mb-6 border-b pb-2 text-sm font-medium">
        <button
          className={tabBtn(activeTab === 'reports')}
          onClick={() => setActiveTab('reports')}>
          گزارش‌ها
        </button>

        <button
          className={tabBtn(activeTab === 'photos')}
          onClick={() => setActiveTab('photos')}>
          عکس‌ها
        </button>

        <button
          className={tabBtn(activeTab === 'weight')}
          onClick={() => setActiveTab('weight')}>
          وزن
        </button>

        <button
          className={tabBtn(activeTab === 'measure')}
          onClick={() => setActiveTab('measure')}>
          اندازه‌ها
        </button>

        <button
          className={tabBtn(activeTab === 'assessment')}
          onClick={() => setActiveTab('assessment')}>
          اطلاعات اولیه
        </button>
        <button
          className={tabBtn(activeTab === 'attachments')}
          onClick={() => setActiveTab('attachments')}>
          آزمایش و بادی آنالیز
        </button>
      </div>

      {/* ---------------- Content ---------------- */}
      <div>
        {activeTab === 'reports' && <ReportsTab userId={userId} />}
        {activeTab === 'photos' && <PhotosTab userId={userId} />}
        {activeTab === 'weight' && <WeightTab userId={userId} />}
        {activeTab === 'measure' && <MeasurementsTab userId={userId} />}
        {activeTab === 'assessment' && <AssessmentTab userId={userId} />}
        {activeTab === 'attachments' && <AttachmentsTab userId={userId} />}
      </div>
    </div>
  );
}

function tabBtn(active) {
  return active
    ? 'px-4 py-2 border-b-2 border-blue-600 text-blue-600'
    : 'px-4 py-2 text-gray-600 hover:text-blue-600';
}
